/**
 * Normalization and deduplication for Missing Chapter Context presentation.
 * Shared by server compiler and client tests.
 */

import type { MissingChapterContext } from '../types/missingChapterContext';

export function normalizeForCompare(text: string): string {
  return text
    .trim()
    .toLowerCase()
    .replace(/[.!?]+$/g, '')
    .replace(/\s+/g, ' ')
    .replace(/^(roadmap beat|roadmap summary|continuity|do not|end with hook|bridge from previous ending|lead into next opening|next chapter assumes|later evidence \(ch\. \d+\)):\s*/gi, '')
    .replace(/^do not:\s*/i, '');
}

export function cleanDisplayText(text: string): string {
  return text
    .trim()
    .replace(/\.{2,}/g, '.')
    .replace(/\s+/g, ' ')
    .replace(/^Do not:\s*Do not\s+/i, 'Do not ')
    .replace(/^Do not:\s*/i, (m, offset, full) => {
      const rest = full.slice(m.length).trim();
      return rest.toLowerCase().startsWith('do not ') ? '' : 'Do not ';
    });
}

export function semanticKey(text: string): string {
  return normalizeForCompare(cleanDisplayText(text));
}

export function dedupeDisplayStrings(items: string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const raw of items) {
    const cleaned = cleanDisplayText(raw);
    if (!cleaned) continue;
    const key = semanticKey(cleaned);
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(cleaned);
  }
  return result;
}

export function dedupeKeywords(keywords: string[]): string[] {
  const seen = new Set<string>();
  return keywords
    .map((k) => k.trim().toLowerCase())
    .filter((k) => {
      if (k.length < 4 || seen.has(k)) return false;
      seen.add(k);
      return true;
    });
}

export function cleanForbiddenMove(text: string): string {
  let t = cleanDisplayText(text);
  t = t.replace(/^do not:\s*/i, '');
  if (/^do not\s+/i.test(t)) {
    return 'Do not ' + t.replace(/^do not\s+/i, '');
  }
  return `Do not ${t.charAt(0).toLowerCase() + t.slice(1)}`;
}

export function statesAreDuplicate(summary?: string, state?: string): boolean {
  if (!summary?.trim() || !state?.trim()) return !state?.trim();
  const a = normalizeForCompare(summary);
  const b = normalizeForCompare(state);
  if (a === b) return true;
  if (a.length > 0 && b.startsWith(a) && b.length - a.length < 25) return true;
  if (b.length > 0 && a.startsWith(b) && a.length - b.length < 25) return true;
  return false;
}

export function deriveDistinctState(summary: string, excerpt: string, fromEnd: boolean): string | undefined {
  if (!excerpt.trim()) return undefined;
  const sentences = excerpt.split(/(?<=[.!?])\s+/).filter(Boolean);
  const derived = (fromEnd ? sentences.slice(-2) : sentences.slice(0, 2)).join(' ').trim();
  if (!derived) return undefined;

  const summaryTrim = summary.trim();
  if (!summaryTrim) return derived;
  if (statesAreDuplicate(summaryTrim, derived)) return undefined;

  if (derived.toLowerCase().startsWith(summaryTrim.toLowerCase())) {
    const remainder = derived.slice(summaryTrim.length).replace(/^[\s.]+/, '').trim();
    return remainder.length > 15 ? remainder : undefined;
  }

  return derived;
}

export interface LaterConsequenceDraft {
  sourceChapterNumber: number;
  sourceType: 'LATER_CHAPTER' | 'LEDGER' | 'ROADMAP';
  evidenceNote: string;
  confidence: number;
  rawEvidenceKeys?: string[];
}

export function formatLaterConsequenceNote(
  missingNumber: number,
  chapterNumber: number,
  combinedText: string,
  matchedKeywords: string[],
): LaterConsequenceDraft {
  const lower = combinedText.toLowerCase();
  const keys = dedupeKeywords(matchedKeywords);
  const hasAlready = /\balready\b/.test(lower);
  const councilVote =
    hasAlready && (/\bcouncil\b/.test(lower) || /\bvote\b/.test(lower) || /\bevacuat/.test(lower));

  if (councilVote) {
    return {
      sourceChapterNumber: chapterNumber,
      sourceType: 'LATER_CHAPTER',
      confidence: 0.88,
      rawEvidenceKeys: keys,
      evidenceNote: `Chapter ${chapterNumber} begins after the council has voted to evacuate, so Chapter ${missingNumber} must establish that decision before Mara departs.`,
    };
  }

  if (hasAlready) {
    return {
      sourceChapterNumber: chapterNumber,
      sourceType: 'LATER_CHAPTER',
      confidence: 0.72,
      rawEvidenceKeys: keys,
      evidenceNote: `Chapter ${chapterNumber} treats events from the missing chapter as already established — Chapter ${missingNumber} must account for that knowledge or change.`,
    };
  }

  if (keys.length > 0) {
    const topic = keys[0];
    return {
      sourceChapterNumber: chapterNumber,
      sourceType: 'LATER_CHAPTER',
      confidence: 0.55,
      rawEvidenceKeys: keys,
      evidenceNote: `Chapter ${chapterNumber} depends on story developments that should occur in Chapter ${missingNumber}, including matters related to ${topic}.`,
    };
  }

  return {
    sourceChapterNumber: chapterNumber,
    sourceType: 'LATER_CHAPTER',
    confidence: 0.4,
    evidenceNote: `Chapter ${chapterNumber} may rely on events that should have happened in Chapter ${missingNumber}.`,
  };
}

export function buildAtAGlanceItems(beats: string[] | undefined, endingHook: string | undefined): string[] {
  const items: string[] = [];
  if (beats?.length) {
    for (const beat of beats) {
      const cleaned = cleanDisplayText(beat);
      if (cleaned) items.push(cleaned.charAt(0).toUpperCase() + cleaned.slice(1));
    }
  }
  if (endingHook?.trim()) {
    const hook = cleanDisplayText(endingHook);
    items.push(`End with ${hook.charAt(0).toLowerCase() + hook.slice(1)}`);
  }
  return dedupeDisplayStrings(items);
}

export interface ClassifiedRequirements {
  atAGlance: string[];
  confirmedRequirements: string[];
  likelyRequirements: string[];
  requiredTransitions: string[];
  uncertainties: string[];
  forbiddenContradictions: string[];
}

export function classifyAndDedupeRequirements(input: {
  roadmapBeats?: string[];
  roadmapSummary?: string;
  roadmapContinuityNotes?: string[];
  roadmapForbidden?: string[];
  roadmapEndingHook?: string;
  previousEndingState?: string;
  nextOpeningState?: string;
  assumedPriorEvents?: string[];
  laterConsequenceNotes?: string[];
  ledgerThreads?: string[];
  hasNeighbors: boolean;
}): ClassifiedRequirements {
  const confirmed: string[] = [];
  const likely: string[] = [];
  const transitions: string[] = [];
  const uncertain: string[] = [];
  const forbidden: string[] = [];

  if (input.roadmapBeats?.length) {
    confirmed.push(...input.roadmapBeats.map((b) => cleanDisplayText(b)));
  }
  if (input.roadmapContinuityNotes?.length) {
    confirmed.push(...input.roadmapContinuityNotes.map((n) => cleanDisplayText(n)));
  }
  if (input.roadmapSummary?.trim()) {
    const s = cleanDisplayText(input.roadmapSummary);
    const beatKeys = new Set((input.roadmapBeats || []).map(semanticKey));
    if (!beatKeys.has(semanticKey(s))) {
      confirmed.push(s);
    }
  }

  for (const event of input.assumedPriorEvents || []) {
    const e = cleanDisplayText(event);
    if (e) likely.push(e);
  }

  for (const note of input.laterConsequenceNotes || []) {
    const n = cleanDisplayText(note);
    if (n) likely.push(n);
  }

  if (input.previousEndingState?.trim()) {
    transitions.push(
      `Connect from the previous chapter ending: ${cleanDisplayText(input.previousEndingState)}`,
    );
  }
  if (input.nextOpeningState?.trim()) {
    transitions.push(
      `Lead into the following chapter opening: ${cleanDisplayText(input.nextOpeningState)}`,
    );
  }

  if (input.roadmapForbidden?.length) {
    forbidden.push(...input.roadmapForbidden.map(cleanForbiddenMove));
  }

  if (!input.roadmapBeats?.length && !input.hasNeighbors) {
    uncertain.push('Limited neighboring chapter data — requirements are inferred from surviving metadata only.');
  }
  if (!input.ledgerThreads?.length) {
    uncertain.push('No unresolved ledger threads were recorded before the gap.');
  }

  const atAGlance = buildAtAGlanceItems(input.roadmapBeats, input.roadmapEndingHook);

  const globalSeen = new Set<string>();
  const takeUnique = (list: string[]) => {
    const out: string[] = [];
    for (const item of dedupeDisplayStrings(list)) {
      const key = semanticKey(item);
      if (globalSeen.has(key)) continue;
      globalSeen.add(key);
      out.push(item);
    }
    return out;
  };

  const glanceKeys = new Set(atAGlance.map(semanticKey));

  return {
    atAGlance,
    confirmedRequirements: takeUnique(confirmed).filter((c) => !glanceKeys.has(semanticKey(c))),
    likelyRequirements: takeUnique(likely),
    requiredTransitions: takeUnique(transitions),
    uncertainties: takeUnique(uncertain),
    forbiddenContradictions: takeUnique(forbidden),
  };
}

function asStringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];
}

export interface NormalizedMissingChapterContext {
  context: MissingChapterContext;
  /** True when the transport payload omitted fields the UI contract expects */
  hadMissingFields: boolean;
}

export function normalizeMissingChapterContext(
  value: Partial<MissingChapterContext> | null | undefined,
): NormalizedMissingChapterContext {
  const arrayFields: (keyof MissingChapterContext)[] = [
    'atAGlance',
    'characterStates',
    'relationshipStates',
    'unresolvedThreads',
    'laterConsequences',
    'confirmedRequirements',
    'likelyRequirements',
    'requiredTransitions',
    'uncertainties',
    'forbiddenContradictions',
  ];

  const hadMissingFields =
    value == null ||
    arrayFields.some((key) => value[key] === undefined) ||
    (value.roadmapEntry != null &&
      (value.roadmapEntry.beats === undefined ||
        value.roadmapEntry.continuityNotes === undefined ||
        value.roadmapEntry.forbiddenMoves === undefined)) ||
    (value.previousChapter != null &&
      (value.previousChapter.activeCharacters === undefined ||
        value.previousChapter.unresolvedThreads === undefined)) ||
    (value.nextChapter != null &&
      (value.nextChapter.activeCharacters === undefined ||
        value.nextChapter.assumedPriorEvents === undefined));

  const laterConsequences = (value?.laterConsequences ?? []).map((c) => ({
    sourceChapterNumber: c?.sourceChapterNumber ?? 0,
    sourceType: c?.sourceType ?? ('LATER_CHAPTER' as const),
    evidenceNote: typeof c?.evidenceNote === 'string' ? c.evidenceNote : '',
    confidence: typeof c?.confidence === 'number' ? c.confidence : 0.5,
    rawEvidenceKeys: asStringArray(c?.rawEvidenceKeys),
  })).filter((c) => c.sourceChapterNumber > 0 && c.evidenceNote.length > 0);

  const context: MissingChapterContext = {
    projectId: value?.projectId ?? '',
    missingChapterNumber: value?.missingChapterNumber ?? 0,
    targetChapterCount: value?.targetChapterCount ?? 0,
    atAGlance: asStringArray(value?.atAGlance),
    previousChapter: value?.previousChapter
      ? {
          ...value.previousChapter,
          activeCharacters: asStringArray(value.previousChapter.activeCharacters),
          unresolvedThreads: asStringArray(value.previousChapter.unresolvedThreads),
        }
      : undefined,
    nextChapter: value?.nextChapter
      ? {
          ...value.nextChapter,
          activeCharacters: asStringArray(value.nextChapter.activeCharacters),
          assumedPriorEvents: asStringArray(value.nextChapter.assumedPriorEvents),
        }
      : undefined,
    roadmapEntry: value?.roadmapEntry
      ? {
          ...value.roadmapEntry,
          beats: asStringArray(value.roadmapEntry.beats),
          continuityNotes: asStringArray(value.roadmapEntry.continuityNotes),
          forbiddenMoves: asStringArray(value.roadmapEntry.forbiddenMoves),
        }
      : undefined,
    characterStates: Array.isArray(value?.characterStates) ? value.characterStates : [],
    relationshipStates: Array.isArray(value?.relationshipStates) ? value.relationshipStates : [],
    unresolvedThreads: asStringArray(value?.unresolvedThreads),
    laterConsequences,
    confirmedRequirements: asStringArray(value?.confirmedRequirements),
    likelyRequirements: asStringArray(value?.likelyRequirements),
    requiredTransitions: asStringArray(value?.requiredTransitions),
    uncertainties: asStringArray(value?.uncertainties),
    forbiddenContradictions: asStringArray(value?.forbiddenContradictions),
  };

  return { context, hadMissingFields };
}
